import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import CrisisSupportBanner from 'src/shared/components/CrisisSupportBanner';

import { navigationService } from 'src/config/rootService';
import { RootState } from 'src/config/rootStore';
import DashboardGreeting from 'src/modules/dashboard/components/DashboardGreeting';
import FallbackCard from 'src/modules/dashboard/components/FallbackCard';
import { getFirstFiveMeditations } from 'src/modules/meditations/selectors/meditationSelectors';
import { ProgramCard } from 'src/modules/newPrograms/components/ProgramCard';
import { useProgramsForPreview } from 'src/modules/newPrograms/hooks/usePrograms';
import { AdditionalProgramsList } from 'src/modules/programs/components/AdditionalProgramsList';
import { AdditionalProgramWeeksList } from 'src/modules/programs/components/AdditionalProgramWeeksList';
import { CorePillarsList } from 'src/modules/programs/cpProgram/components/CorePillarsList';
import { ProgramEnum } from 'src/modules/programs/models/programs';
import { getMyProviders } from 'src/modules/providers/selectors/providersSelectors';
import { DailyPauseCard } from 'src/modules/scheduledContent/components/DailyPauseCard';
import { SessionsWidget } from 'src/modules/videoSessions/components/SessionsWidget';
import { ContentCard } from 'src/shared/components/ContentCard';
import { isCurrentLanguageSupported } from 'src/shared/helpers/isCurrentLanguageSupported';
import { useLocalizedVersion } from 'src/shared/hooks/useLocalizedVersion';
import { color, font, layout } from 'src/shared/styles/variables';

const DashboardWrapper = styled.div({
    backgroundColor: color.white,
    flexGrow: 1,
});

const BannerContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 28,
    marginLeft: -10,
    marginRight: -10,
});

const DashboardContent = styled.div({
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    padding: '1rem 1.5rem',
});

const SectionTitle = styled.p(
    {
        color: color.pickledBluewood,
    },
    font.heavy(font.size.large),
);

const SectionHeaderWrapper = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 20,
    alignItems: 'center',
});

const SectionWrapper = styled.div({
    display: 'flex',
    justifyContent: 'flex-start',
    margin: '0 -0.5%',
    flexWrap: 'wrap',
});

const Item = styled.div({
    display: 'flex',
    flex: '0 0 215px',
    height: 'auto',
    margin: '0 0.5%',
});

const ShowAllButton = styled.p(
    {
        borderRadius: 5,
        padding: '11px 0.75% 8px 14px',
        color: color.steelGray,
        cursor: 'pointer',
        transition: '.3s background-color',
        lineHeight: '14px',

        ':hover': {
            backgroundColor: '#D9DFEA',
        },
    },
    font.heavy(font.size.medium),
);

interface MatchParams {
    sectionState: ProgramEnum;
}

export enum DashboardTestID {
    Container = 'DashboardTestID.Container',
    MeditationsSection = 'DashboardTestID.MeditationsSection',
    ProgramsSection = 'DashboardTestID.ProgramsSection',
    ProgramsSectionShowAll = 'DashboardTestID.ProgramsSectionShowAll',
}

type Props = ReturnType<typeof mapStateToProps> &
    RouteComponentProps<MatchParams, {}, {}> &
    WithTranslation;

// TODO: Should be removed once home screen revamp phase 1 is fully rolled out
export const Dashboard: React.FC<Props> = ({ match, meditations, myProviders, t, i18n }) => {
    const [localizedVersion] = useLocalizedVersion();
    const { showBackendPrograms, showDailyPauseOnWeb } = useFlags();
    const programs = useProgramsForPreview();
    const isLanguageSupported = isCurrentLanguageSupported();
    const { sectionState } = match.params;

    return (
        <DashboardWrapper data-testid={DashboardTestID.Container}>
            <DashboardContent>
                <DashboardGreeting />
                <BannerContainer>
                    {!showDailyPauseOnWeb || localizedVersion ? (
                        <FallbackCard providers={myProviders} />
                    ) : (
                        <DailyPauseCard fallback={<FallbackCard providers={myProviders} />} />
                    )}

                    <SessionsWidget />
                </BannerContainer>

                {!!meditations.length && isLanguageSupported && (
                    <>
                        <SectionHeaderWrapper>
                            <SectionTitle>{t('dashboard:meditations')}</SectionTitle>
                            <ShowAllButton
                                onClick={() => navigationService.navigate('/meditations')}
                            >
                                {t('dashboard:show_all')}
                            </ShowAllButton>
                        </SectionHeaderWrapper>

                        <SectionWrapper data-testid={DashboardTestID.MeditationsSection}>
                            {meditations.map(meditation => (
                                <Item key={meditation.id}>
                                    <ContentCard
                                        title={meditation.name}
                                        image={meditation.image}
                                        firstInfoText={t(
                                            'onboarding:your_on_your_way_screen.meditation',
                                        )}
                                        secondInfoText={t('dashboard:meditations_duration', {
                                            count: meditation.duration,
                                        })}
                                        slug={meditation.slug ? meditation.slug : ''}
                                        onClick={() =>
                                            navigationService.navigate(
                                                '/meditation/' + meditation.slug,
                                            )
                                        }
                                    />
                                </Item>
                            ))}
                        </SectionWrapper>
                    </>
                )}

                {!localizedVersion && showBackendPrograms && !!programs.length && (
                    <>
                        <SectionHeaderWrapper>
                            <SectionTitle>{t('dashboard:programs')}</SectionTitle>
                            <ShowAllButton
                                onClick={() => navigationService.navigate('/programs')}
                                data-testid={DashboardTestID.ProgramsSectionShowAll}
                            >
                                {t('dashboard:show_all')}
                            </ShowAllButton>
                        </SectionHeaderWrapper>

                        <SectionWrapper data-testid={DashboardTestID.ProgramsSection}>
                            {programs.map(program => (
                                <Item key={program.id}>
                                    <ProgramCard program={program} />
                                </Item>
                            ))}
                        </SectionWrapper>
                    </>
                )}

                {!localizedVersion && !showBackendPrograms && (
                    <>
                        {sectionState ? (
                            <AdditionalProgramWeeksList programId={sectionState} />
                        ) : (
                            <>
                                <CorePillarsList noMargin webExclusive />
                                {i18n.language === 'en-Us' && (
                                    <AdditionalProgramsList webExclusive />
                                )}
                            </>
                        )}
                    </>
                )}

                <CrisisSupportBanner />
            </DashboardContent>
        </DashboardWrapper>
    );
};

const mapStateToProps = (state: RootState) => ({
    meditations: getFirstFiveMeditations(state),
    myProviders: getMyProviders(state),
});

export default withTranslation()(connect(mapStateToProps)(Dashboard));
